import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Server, Database, Activity, RefreshCw } from 'lucide-react';

const WFAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '/api';
  
  useEffect(() => {
    fetchSystemData();
    const interval = setInterval(fetchSystemData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSystemData = async () => {
    try {
      setRefreshing(true);
      const [systemResponse, healthResponse] = await Promise.all([
        fetch(`${API_BASE}/system/metrics`),
        fetch(`${API_BASE}/health/detailed`)
      ]);
      
      if (systemResponse.ok && healthResponse.ok) {
        const systemData = await systemResponse.json();
        const healthData = await healthResponse.json();
        setSystemStats({ ...systemData, health: healthData });
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch system data:', error);
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'infrastructure', label: 'Infrastructure', icon: Server },
    { id: 'database', label: 'Database', icon: Database },
  ];

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-900">System Overview</h2>
        <button
          onClick={fetchSystemData}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {systemStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CPU Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {systemStats.cpu ? `${systemStats.cpu.usage.toFixed(1)}%` : 'N/A'}
                </p>
              </div>
              <Activity className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Memory Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {systemStats.memory ? `${systemStats.memory.usagePercent}%` : 'N/A'}
                </p>
              </div>
              <Server className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Disk Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {systemStats.disk ? `${systemStats.disk.usagePercent}%` : 'N/A'}
                </p>
              </div>
              <Database className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Information</h3>
        {systemStats?.system && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">Hostname:</span>
              <span className="ml-2 text-gray-900">{systemStats.system.hostname}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Platform:</span>
              <span className="ml-2 text-gray-900">{systemStats.system.platform}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Architecture:</span>
              <span className="ml-2 text-gray-900">{systemStats.system.arch}</span>
            </div>
            <div>
              <span className="font-medium text-gray-600">Uptime:</span>
              <span className="ml-2 text-gray-900">{Math.floor(systemStats.system.uptime / 86400)} days</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStudioIntegration = () => (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gray-900">Database Management</h2>
      <div className="bg-white p-6 rounded-lg shadow">
        <p className="text-gray-600 mb-4">Access Supabase Studio for database management:</p>
        <a
          href="https://studio.workflowfather.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Open Supabase Studio
          <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );

  const renderInfrastructure = () => (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gray-900">Infrastructure Management</h2>
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">VPS Management</h3>
        <p className="text-gray-600 mb-4">VPS operations are managed via MCP integration.</p>
        <div className="grid grid-cols-2 gap-4">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Create Snapshot
          </button>
          <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
            Restart VPS
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'database':
        return renderStudioIntegration();
      case 'infrastructure':
        return renderInfrastructure();
      default:
        return (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h2>
            <p className="text-gray-600">This section is under development.</p>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <div className="w-64 bg-white shadow-lg">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">WF Admin</h1>
          <p className="text-sm text-gray-600">Phase 3B Dashboard</p>
        </div>
        
        <nav className="mt-6">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center space-x-3 px-6 py-3 text-left hover:bg-gray-50 transition-colors ${
                  activeTab === item.id ? 'bg-blue-50 border-r-2 border-blue-600 text-blue-600' : 'text-gray-700'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default WFAdminDashboard;
